
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Eye, Users, TrendingUp, DollarSign } from "lucide-react";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Line, LineChart, ResponsiveContainer, XAxis, YAxis } from "recharts";

interface OverviewMetricsProps {
  timeframe: "7d" | "30d" | "90d" | "1y";
}

// Sample data - in a real app, this would come from an API
const generateData = (timeframe: string) => {
  const days = timeframe === "7d" ? 7 : timeframe === "30d" ? 30 : timeframe === "90d" ? 90 : 365;
  const data = [];
  const now = new Date();

  for (let i = days; i >= 0; i--) {
    const date = new Date();
    date.setDate(now.getDate() - i);
    data.push({
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      reads: Math.floor(1000 + Math.random() * 500),
      subscribers: Math.floor(200 + Math.random() * 50),
      revenue: Math.floor(500 + Math.random() * 300),
    });
  }
  return data;
};

const OverviewMetrics: React.FC<OverviewMetricsProps> = ({ timeframe }) => {
  const data = generateData(timeframe);
  
  const totalReads = data.reduce((sum, item) => sum + item.reads, 0);
  const newSubscribers = data.reduce((sum, item) => sum + Math.floor(Math.random() * 3), 0);
  const totalRevenue = data.reduce((sum, item) => sum + item.revenue, 0);
  
  // Calculate growth rates
  const growth = {
    reads: Math.floor(Math.random() * 20) + 5,
    subscribers: Math.floor(Math.random() * 15) + 3,
    revenue: Math.floor(Math.random() * 25) + 8,
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="dark:bg-gray-800 dark:text-gray-100">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Reads</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalReads.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              <span className="text-green-500 inline-flex items-center">
                <TrendingUp className="h-3 w-3 mr-1" />+{growth.reads}%
              </span>{" "}
              from previous period
            </p>
          </CardContent>
        </Card>
        
        <Card className="dark:bg-gray-800 dark:text-gray-100">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">New Subscribers</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{newSubscribers}</div>
            <p className="text-xs text-muted-foreground">
              <span className="text-green-500 inline-flex items-center">
                <TrendingUp className="h-3 w-3 mr-1" />+{growth.subscribers}%
              </span>{" "}
              from previous period
            </p>
          </CardContent>
        </Card>
        
        <Card className="dark:bg-gray-800 dark:text-gray-100">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${totalRevenue.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              <span className="text-green-500 inline-flex items-center">
                <TrendingUp className="h-3 w-3 mr-1" />+{growth.revenue}%
              </span>{" "}
              from previous period
            </p>
          </CardContent>
        </Card>
        
        <Card className="dark:bg-gray-800 dark:text-gray-100">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg. Reading Time</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">3m 42s</div>
            <p className="text-xs text-muted-foreground">
              <span className="text-green-500 inline-flex items-center">
                <TrendingUp className="h-3 w-3 mr-1" />+12%
              </span>{" "}
              from previous period
            </p>
          </CardContent>
        </Card>
      </div>
      
      <Card className="dark:bg-gray-800 dark:text-gray-100">
        <CardHeader>
          <CardTitle>Performance Trends</CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer
            config={{
              reads: { label: "Reads", color: "#F97316" },
              subscribers: { label: "Subscribers", color: "#2563EB" },
              revenue: { label: "Revenue ($)", color: "#10B981" }
            }}
            className="h-80"
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data}>
                <XAxis
                  dataKey="date"
                  stroke="#888888"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke="#888888"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => `${value}`}
                />
                <Line
                  type="monotone"
                  dataKey="reads"
                  stroke="var(--color-reads, #F97316)"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="subscribers"
                  stroke="var(--color-subscribers, #2563EB)"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="var(--color-revenue, #10B981)"
                  strokeWidth={2}
                  dot={false}
                />
                <ChartTooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <ChartTooltipContent payload={payload} />
                      );
                    }
                    return null;
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardContent>
      </Card>
    </div>
  );
};

export default OverviewMetrics;
